
import React from "react";
import { Table, Button, Space, Dropdown, Typography } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import {
  MoreOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { VaultFile } from "@/types";
import { FileIcon, ShareBadge } from "@/components/shared";
import { formatFileSize, formatRelativeDate } from "@/utils/formatters";

const { Text } = Typography;

interface FileTableProps {
  files: VaultFile[];
  isLoading?: boolean;
  selectedIds: Set<string>;
  onFileClick: (file: VaultFile, e: React.MouseEvent) => void;
  onFileDoubleClick: (file: VaultFile) => void;
  onDownload: (file: VaultFile) => void;
  onShare: (file: VaultFile) => void;
  onRename: (file: VaultFile) => void;
  onDelete: (file: VaultFile) => void;
  onSelectionChange: (selectedIds: string[]) => void;
  canShare?: boolean;
}

export default function FileTable({
  files,
  isLoading = false,
  selectedIds,
  onFileClick,
  onFileDoubleClick,
  onDownload,
  onShare,
  onRename,
  onDelete,
  onSelectionChange,
  canShare = true,
}: FileTableProps) {
  const getMenuItems = (file: VaultFile): MenuProps["items"] => [
    {
      key: "download",
      icon: <DownloadOutlined />,
      label: "Download",
      onClick: () => onDownload(file),
    },
    ...(canShare
      ? [
          {
            key: "share",
            icon: <ShareAltOutlined />,
            label: "Share",
            onClick: () => onShare(file),
          },
        ]
      : []),
    { type: "divider" },
    {
      key: "rename",
      icon: <EditOutlined />,
      label: "Rename",
      onClick: () => onRename(file),
    },
    { type: "divider" },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      danger: true,
      onClick: () => onDelete(file),
    },
  ];

  const columns: ColumnsType<VaultFile> = [
    {
      title: "Name",
      dataIndex: "fileName",
      key: "fileName",
      sorter: (a, b) => a.fileName.localeCompare(b.fileName),
      render: (fileName, record) => (
        <Space>
          <FileIcon mimeType={record.mimeType} size="small" />
          <Text
            ellipsis
            style={{ maxWidth: 300 }}
            title={fileName}
          >
            {fileName}
          </Text>
          {record.sharedWith?.length > 0 && <ShareBadge />}
        </Space>
      ),
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 100,
      sorter: (a, b) => a.size - b.size,
      render: (size) => (
        <Text type="secondary">{formatFileSize(size)}</Text>
      ),
    },
    {
      title: "Modified",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 120,
      sorter: (a, b) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (date) => (
        <Text type="secondary">{formatRelativeDate(date)}</Text>
      ),
    },
    {
      title: "Created by",
      dataIndex: ["uploadedBy", "name"],
      key: "uploadedBy",
      width: 140,
      render: (name) => (
        <Text type="secondary" ellipsis style={{ maxWidth: 120 }}>
          {name}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, record) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const rowSelection: TableProps<VaultFile>["rowSelection"] = {
    selectedRowKeys: Array.from(selectedIds),
    onChange: (selectedRowKeys) => {
      onSelectionChange(selectedRowKeys as string[]);
    },
    columnWidth: 48,
  };

  return (
    <div>
      <div className="section-label">FILES</div>
      <Table
        columns={columns}
        dataSource={files}
        rowKey="id"
        rowSelection={rowSelection}
        loading={isLoading}
        pagination={false}
        size="middle"
        onRow={(record) => ({
          onClick: (e) => onFileClick(record, e),
          onDoubleClick: () => onFileDoubleClick(record),
          style: {
            cursor: "pointer",
            backgroundColor: selectedIds.has(record.id)
              ? "rgba(15, 110, 86, 0.04)"
              : undefined,
          },
        })}
      />
    </div>
  );
}
